"use client"

import { FormEvent, startTransition, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import {
  UsedFilterOptions,
  UsedFilters,
} from "@lib/util/used-listing"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

type UsedFilterBarProps = {
  sortBy: SortOptions
  filters: UsedFilters
  options: UsedFilterOptions
  count: number
}

const sortOptions: Array<{ value: SortOptions; label: string }> = [
  { value: "created_at", label: "ล่าสุด" },
  { value: "price_asc", label: "ราคาต่ำไปสูง" },
  { value: "price_desc", label: "ราคาสูงไปต่ำ" },
]

export default function UsedFilterBar({
  sortBy,
  filters,
  options,
  count,
}: UsedFilterBarProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [query, setQuery] = useState(filters.q || "")

  const updateParam = (name: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(name, value)
    } else {
      params.delete(name)
    }
    params.delete("page")

    startTransition(() => {
      const next = params.toString()
      router.push(next ? `${pathname}?${next}` : pathname)
    })
  }

  const clearFilters = () => {
    setQuery("")
    startTransition(() => {
      router.push(pathname)
    })
  }

  const onSearch = (event: FormEvent) => {
    event.preventDefault()
    updateParam("q", query.trim())
  }

  const hasActiveFilters = Boolean(
    filters.brand ||
      filters.category ||
      filters.condition ||
      filters.availability === "all" ||
      filters.q
  )

  return (
    <div className="mb-8 flex flex-col gap-4 rounded-xl border border-[#e8eaed] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="flex flex-col gap-3 small:flex-row small:items-end small:justify-between">
        <form onSubmit={onSearch} className="flex min-w-0 flex-1 items-end gap-2">
          <label className="catalog-filter-select min-w-0 flex-1">
            <span>ค้นหารุ่น</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ชื่อรุ่น หรือแบรนด์"
              className="bg-transparent text-[13px] font-semibold text-mt-ink outline-none placeholder:font-normal placeholder:text-mt-muted"
              data-testid="used-search"
            />
          </label>
          <button
            type="submit"
            className="rounded-lg border border-[#d7dadd] bg-white px-4 py-2.5 text-[12px] font-semibold text-mt-ink hover:border-vx-accent hover:text-vx-accent"
          >
            ค้นหา
          </button>
        </form>
        <p className="text-sm text-mt-muted" data-testid="used-result-count">
          พบ {count} รายการ
        </p>
      </div>

      <div className="flex flex-col gap-3 medium:flex-row medium:items-end medium:justify-between">
        <div className="grid grid-cols-2 gap-2 small:grid-cols-4">
          <label className="catalog-filter-select">
            <span>แบรนด์</span>
            <select
              value={filters.brand || ""}
              onChange={(event) => updateParam("brand", event.target.value)}
              data-testid="used-filter-brand"
            >
              <option value="">ทั้งหมด</option>
              {options.brand.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </label>
          <label className="catalog-filter-select">
            <span>ประเภท</span>
            <select
              value={filters.category || ""}
              onChange={(event) => updateParam("category", event.target.value)}
              data-testid="used-filter-category"
            >
              <option value="">ทั้งหมด</option>
              {options.category.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="catalog-filter-select">
            <span>สภาพ</span>
            <select
              value={filters.condition || ""}
              onChange={(event) => updateParam("condition", event.target.value)}
              data-testid="used-filter-condition"
            >
              <option value="">ทั้งหมด</option>
              {options.condition.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="catalog-filter-select">
            <span>สถานะ</span>
            <select
              value={filters.availability === "all" ? "all" : ""}
              onChange={(event) =>
                updateParam("availability", event.target.value)
              }
              data-testid="used-filter-availability"
            >
              <option value="">ยังมี</option>
              <option value="all">รวมที่ขายแล้ว</option>
            </select>
          </label>
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              type="button"
              className="catalog-filter-reset"
              onClick={clearFilters}
            >
              ล้างตัวกรอง
            </button>
          )}
          <label className="catalog-filter-select">
            <span>เรียง</span>
            <select
              value={sortBy}
              onChange={(event) => updateParam("sortBy", event.target.value)}
              data-testid="used-sort"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  )
}
